<template>
  <div class="achieve-dashboard">

    <!-- Charts Section -->
    <div class="mb-6 relative">
      <!-- Loading overlay for charts -->
      <div v-if="chartsLoading" class="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
        <div class="flex flex-col items-center gap-4">
          <div class="charts-spinner"></div>
          <div class="text-sm font-semibold text-gray-600">{{ t('views.achieve_dashboard.loading_dashboard_data') }}</div>
        </div>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
      <!-- Village Status Totals -->
      <div class="bg-white overflow-hidden shadow rounded-lg p-4">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">{{ t('views.achieve_dashboard.village_status_totals') }}</h3>
        <div class="grid grid-cols-2 gap-4">
          <div class="bg-gray-50 rounded-lg p-4 border border-gray-200 col-span-2">
            <div class="text-sm text-gray-600 mb-1">{{ t('views.achieve_dashboard.total_villages') }}</div>
            <div class="text-2xl font-bold text-gray-900">{{ formatNumber(totalVillages) }}</div>
          </div>
            <div class="bg-red-50 rounded-lg p-4 border-2 border-red-300">
            <div class="text-sm font-semibold text-red-700 mb-1">{{ t('views.achieve_dashboard.red') }}</div>
            <div class="text-3xl font-bold text-red-800">{{ formatNumber(redVillages) }}</div>
          </div>
          <div class="bg-green-50 rounded-lg p-4 border-2 border-green-300">
            <div class="text-sm font-semibold text-green-700 mb-1">{{ t('views.achieve_dashboard.green') }}</div>
            <div class="text-3xl font-bold text-green-800">{{ formatNumber(greenVillages) }}</div>
          </div>
          <div class="bg-yellow-50 rounded-lg p-4 border-2 border-yellow-300">
            <div class="text-sm font-semibold text-yellow-700 mb-1">{{ t('views.achieve_dashboard.yellow') }}</div>
            <div class="text-3xl font-bold text-yellow-800">{{ formatNumber(yellowVillages) }}</div>
          </div>
          <div class="bg-gray-50 rounded-lg p-4 border-2 border-gray-300">
            <div class="text-sm font-semibold text-gray-700 mb-1">{{ t('views.achieve_dashboard.white') }}</div>
            <div class="text-3xl font-bold text-gray-800">{{ formatNumber(whiteVillages) }}</div>
          </div>
        </div>
      </div>

      <!-- Status for Villages with Data (Donut Chart) -->
      <div class="bg-white overflow-hidden shadow rounded-lg p-4">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">{{ t('views.achieve_dashboard.status_for_villages_with_data') }}</h3>
        <div class="relative" style="height: 250px;">
          <canvas ref="donutChart"></canvas>
          <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div class="text-center">
              <div class="text-2xl font-bold text-gray-900">{{ formatCompactNumber(villagesWithData) }}</div>
            </div>
          </div>
        </div>
        <div class="mt-4 flex flex-wrap justify-center gap-4">
          <div class="flex items-center gap-2">
            <div class="w-4 h-4 rounded bg-yellow-400"></div>
            <span class="text-sm text-gray-700">{{ t('views.achieve_dashboard.has_christians_no_churches') }}</span>
          </div>
          <div class="flex items-center gap-2">
            <div class="w-4 h-4 rounded bg-red-500"></div>
            <span class="text-sm text-gray-700">{{ t('views.achieve_dashboard.no_christians_no_churches') }}</span>
          </div>
          <div class="flex items-center gap-2">
            <div class="w-4 h-4 rounded bg-green-500"></div>
            <span class="text-sm text-gray-700">{{ t('views.achieve_dashboard.has_churches') }}</span>
          </div>
        </div>
      </div>

      <!-- Villages with Data vs All Villages (Gauge Chart) -->
      <div class="bg-white overflow-hidden shadow rounded-lg p-4">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">{{ t('views.achieve_dashboard.villages_with_data_vs_all_villages') }}</h3>
        <div class="relative" style="height: 250px;">
          <canvas ref="gaugeChart"></canvas>
          <div class="absolute bottom-4 left-0 right-0 text-center pointer-events-none">
            <div class="text-xl font-bold text-gray-900">{{ villagesWithDataPercent }}%</div>
          </div>
        </div>
        <div class="mt-4 flex flex-wrap justify-center gap-3 text-xs">
          <div class="flex items-center gap-1.5">
            <div class="w-3 h-3 rounded bg-blue-500"></div>
            <span class="text-gray-700">{{ t('views.achieve_dashboard.with_data') }}: {{ formatNumber(villagesWithData) }}</span>
          </div>
          <div class="flex items-center gap-1.5">
            <div class="w-3 h-3 rounded bg-gray-300"></div>
            <span class="text-gray-700">{{ t('views.achieve_dashboard.village_count') }}: {{ formatNumber(totalVillages) }}</span>
          </div>
        </div>
      </div>
      </div>
    </div>

    <!-- View State Toggle Section -->
    <h3 class="text-lg leading-6 font-medium text-gray-900 block pr-6 pb-4">
          <span class="inline-flex">
            Target Area Stats - {{ viewState === 'active' ? 'Active' : 'All' }}
          </span>
            <button @click="toggleViewState" class="inline-flex items-center px-4 py-2 ml-3 text-sm leading-5 font-medium rounded-md border-transparent text-white bg-blue-600 hover:bg-blue-500 focus:outline-0 focus:ring-blue focus:border-blue-700 active:bg-blue-700">
              {{ viewState === 'active' ? 'View All' : 'View Active' }}
            </button>
    </h3>

    <!-- Kestrel Map Section -->
    <div class="mb-6 bg-white overflow-hidden shadow rounded-lg p-4">
      <h3 class="text-lg font-semibold text-gray-900 mb-4">{{ t('views.achieve_dashboard.target_area_map_title') }}</h3>
      <div style="height: 650px;">
        <TargetAreas
          :target-areas-data="targetAreasData"
          :api-data="apiData"
        />
      </div>
    </div>

    <!-- Target Area Stats Boxes Section -->
    <div class="mb-6 bg-white overflow-hidden shadow rounded-lg p-4"">
      <h3 class="text-lg font-semibold text-gray-900 mb-4">{{ t('views.achieve_dashboard.target_area_stats_title') }}</h3>
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4" v-show="false">
        <div class="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div class="text-sm text-gray-600 mb-1">{{ t('views.achieve_dashboard.total_villages') }}</div>
          <div class="text-2xl font-bold text-gray-900">{{ formatNumber(totalVillages) }}</div>
        </div>
        <div class="bg-red-50 rounded-lg p-4 border border-red-200">
          <div class="text-sm text-red-600 mb-1">{{ t('views.achieve_dashboard.red_villages') }}</div>
          <div class="text-2xl font-bold text-red-700">{{ formatNumber(redVillages) }}</div>
        </div>
        <div class="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
          <div class="text-sm text-yellow-600 mb-1">{{ t('views.achieve_dashboard.yellow_villages') }}</div>
          <div class="text-2xl font-bold text-yellow-700">{{ formatNumber(yellowVillages) }}</div>
        </div>
        <div class="bg-green-50 rounded-lg p-4 border border-green-200">
          <div class="text-sm text-green-600 mb-1">{{ t('views.achieve_dashboard.green_villages') }}</div>
          <div class="text-2xl font-bold text-green-700">{{ formatNumber(greenVillages) }}</div>
        </div>
      </div>
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div class="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div class="text-sm text-gray-600 mb-1">{{ t('views.achieve_dashboard.target_areas') }}</div>
          <div class="text-2xl font-bold text-gray-900">{{ formatNumber(targetAreaCount) }}</div>
        </div>
        <div class="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div class="text-sm text-gray-600 mb-1">{{ t('views.achieve_dashboard.villages_targeted') }}</div>
          <div class="text-2xl font-bold text-gray-900">{{ formatNumber(targetVillages) }}</div>
        </div>
        <div class="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div class="text-sm text-gray-600 mb-1">{{ t('views.achieve_dashboard.target_villages_surveyed') }}</div>
          <div class="text-2xl font-bold text-gray-900">{{ formatNumber(surveyedVillages) }}</div>
        </div>
        <div class="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <div class="text-sm text-blue-600 mb-1">{{ t('views.achieve_dashboard.survey_complete_percentage') }}</div>
          <div class="text-2xl font-bold text-blue-700">{{ surveyCompletePercent }}%</div>
        </div>
      </div>
    </div>

    <!-- Target Areas Drill-Down Table -->
    <div class="mb-6 bg-white overflow-hidden shadow rounded-lg p-4">
      <h3 class="text-lg font-semibold text-gray-900 mb-4">{{ t('views.achieve_dashboard.target_areas_table_title') }}</h3>
      
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('views.achieve_dashboard.table_name') }}</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('views.achieve_dashboard.table_villages') }}</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-red-500 uppercase tracking-wider">{{ t('views.achieve_dashboard.table_red') }}</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-yellow-600 uppercase tracking-wider">{{ t('views.achieve_dashboard.table_yellow') }}</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-green-600 uppercase tracking-wider">{{ t('views.achieve_dashboard.table_green') }}</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('views.achieve_dashboard.table_white') }}</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-blue-600 uppercase tracking-wider">{{ t('views.achieve_dashboard.table_surveyed') }}</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <!-- TCG Rows -->
            <template v-for="tcg in enrichedTableData" :key="'tcg-' + tcg.id">
              <!-- TCG Header Row -->
              <tr 
                class="bg-gray-50 hover:bg-gray-100 cursor-pointer"
                @click="toggleTcgExpanded(tcg.id)"
              >
                <td class="px-4 py-3 text-sm font-semibold text-gray-900">
                  <span class="inline-flex items-center gap-2">
                    <svg 
                      class="w-4 h-4 transition-transform" 
                      :class="{ 'rotate-90': expandedTcgs.includes(tcg.id) }"
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                    </svg>
                    {{ tcg.name }}
                  </span>
                </td>
                <td class="px-4 py-3 text-sm text-center text-gray-700 font-medium">{{ tcg.stats.village_count }}</td>
                <td class="px-4 py-3 text-sm text-center text-red-700 font-medium">{{ tcg.stats.red_village_count }}</td>
                <td class="px-4 py-3 text-sm text-center text-yellow-700 font-medium">{{ tcg.stats.yellow_village_count }}</td>
                <td class="px-4 py-3 text-sm text-center text-green-700 font-medium">{{ tcg.stats.green_village_count }}</td>
                <td class="px-4 py-3 text-sm text-center text-gray-600 font-medium">{{ tcg.stats.white_village_count }}</td>
                <td class="px-4 py-3 text-sm text-center text-blue-700 font-medium">{{ tcg.stats.surveyed_village_count }}</td>
              </tr>
              
              <!-- TC Rows (children of TCG) -->
              <template v-if="expandedTcgs.includes(tcg.id)">
                <template v-for="tc in tcg.training_centers" :key="'tc-' + tc.id">
                  <tr 
                    class="hover:bg-blue-50 cursor-pointer"
                    :class="{ 'bg-blue-50': expandedTcs.includes(tc.id) }"
                    @click="toggleTcExpanded(tc.id)"
                  >
                    <td class="px-4 py-2 text-sm text-gray-700 pl-10">
                      <span class="inline-flex items-center gap-2">
                        <svg 
                          v-if="tc.target_area"
                          class="w-3 h-3 transition-transform text-gray-400" 
                          :class="{ 'rotate-90': expandedTcs.includes(tc.id) }"
                          fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        >
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                        </svg>
                        <span v-else class="w-3 h-3"></span>
                        <span class="font-medium">{{ tc.paul_name }}</span>
                        <span class="text-gray-400">·</span>
                        <span class="text-gray-500">{{ tc.name }}</span>
                      </span>
                    </td>
                    <td class="px-4 py-2 text-sm text-center text-gray-600">{{ tc.target_area?.stats?.village_count || 0 }}</td>
                    <td class="px-4 py-2 text-sm text-center text-red-600">{{ tc.target_area?.stats?.red_village_count || 0 }}</td>
                    <td class="px-4 py-2 text-sm text-center text-yellow-600">{{ tc.target_area?.stats?.yellow_village_count || 0 }}</td>
                    <td class="px-4 py-2 text-sm text-center text-green-600">{{ tc.target_area?.stats?.green_village_count || 0 }}</td>
                    <td class="px-4 py-2 text-sm text-center text-gray-500">{{ tc.target_area?.stats?.white_village_count || 0 }}</td>
                    <td class="px-4 py-2 text-sm text-center text-blue-600">{{ tc.target_area?.stats?.surveyed_village_count || 0 }}</td>
                  </tr>
                  
                  <!-- Target Area Row (child of TC) -->
                  <tr 
                    v-if="expandedTcs.includes(tc.id) && tc.target_area"
                    class="bg-gray-50 hover:bg-blue-50 cursor-pointer transition-colors"
                    @click="navigateToTargetArea(tc.id)"
                    :title="t('views.achieve_dashboard.click_to_view_target_area')"
                  >
                    <td class="px-4 py-2 text-sm text-gray-600 pl-16">
                      <span class="inline-flex items-center gap-2">
                        <svg class="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"/>
                        </svg>
                        <span class="text-blue-600 hover:text-blue-800 font-medium">{{ tc.target_area.name }}</span>
                        <svg class="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                        </svg>
                      </span>
                    </td>
                    <td class="px-4 py-2 text-sm text-center text-gray-600">{{ tc.target_area.stats.village_count || 0 }}</td>
                    <td class="px-4 py-2 text-sm text-center text-red-600">{{ tc.target_area.stats.red_village_count || 0 }}</td>
                    <td class="px-4 py-2 text-sm text-center text-yellow-600">{{ tc.target_area.stats.yellow_village_count || 0 }}</td>
                    <td class="px-4 py-2 text-sm text-center text-green-600">{{ tc.target_area.stats.green_village_count || 0 }}</td>
                    <td class="px-4 py-2 text-sm text-center text-gray-500">{{ tc.target_area.stats.white_village_count || 0 }}</td>
                    <td class="px-4 py-2 text-sm text-center text-blue-600">{{ tc.target_area.stats.surveyed_village_count || 0 }}</td>
                  </tr>
                </template>
              </template>
            </template>
            
            <!-- Empty State -->
            <tr v-if="enrichedTableData.length === 0">
              <td colspan="7" class="px-4 py-8 text-center text-gray-500">
                {{ t('views.achieve_dashboard.no_target_areas') }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Churches Stats Section -->
    <div class="mb-6 bg-white overflow-hidden shadow rounded-lg p-4">
      <h3 class="text-lg font-semibold text-gray-900 mb-4">{{ t('views.achieve_dashboard.churches_stats_title') }}</h3>
      <div class="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div class="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div class="text-sm text-gray-600 mb-1">{{ t('views.achieve_dashboard.total_churches') }}</div>
          <div class="text-2xl font-bold text-gray-900">{{ formatNumber(totalChurches) }}</div>
        </div>
        <div class="bg-red-50 rounded-lg p-4 border border-red-200">
          <div class="text-sm text-red-600 mb-1">{{ t('views.achieve_dashboard.red_village') }}</div>
          <div class="text-2xl font-bold text-red-700">{{ formatNumber(churchesInRedVillages) }} <span class="text-base font-medium">({{ churchesRedPercent }}%)</span></div>
        </div>
        <div class="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
          <div class="text-sm text-yellow-600 mb-1">{{ t('views.achieve_dashboard.yellow_village') }}</div>
          <div class="text-2xl font-bold text-yellow-700">{{ formatNumber(churchesInYellowVillages) }} <span class="text-base font-medium">({{ churchesYellowPercent }}%)</span></div>
        </div>
        <div class="bg-green-50 rounded-lg p-4 border border-green-200">
          <div class="text-sm text-green-600 mb-1">{{ t('views.achieve_dashboard.green_village') }}</div>
          <div class="text-2xl font-bold text-green-700">{{ formatNumber(churchesInGreenVillages) }} <span class="text-base font-medium">({{ churchesGreenPercent }}%)</span></div>
        </div>
        <div class="bg-gray-100 rounded-lg p-4 border border-gray-300">
          <div class="text-sm text-gray-600 mb-1">{{ t('views.achieve_dashboard.white_village') }}</div>
          <div class="text-2xl font-bold text-gray-700">{{ formatNumber(churchesInWhiteVillages) }} <span class="text-base font-medium">({{ churchesWhitePercent }}%)</span></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { Chart, Tooltip, CategoryScale, LinearScale, Title, ArcElement, Legend, DoughnutController } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import i18n from '@utils/i18n'
import { useSparrowAuth } from '@composables/useSparrowAuth'
import SparrowService from '@services/SparrowService'
import TargetAreas from '@components/kestrel/v2/TargetAreas.vue'

const { t } = i18n.global

const props = defineProps({
  targetAreasData: {
    type: Array,
    required: true
  },
  churchPresenceStats: {
    type: Object,
    default: () => ({
      total_churches: 0,
      churches_red: 0,
      churches_yellow: 0,
      churches_green: 0,
      churches_white: 0,
    })
  },
  targetAreasTableData: {
    type: Array,
    default: () => []
  },
  apiData: {
    type: Object,
    required: true
  },
})

// Chart refs
const donutChart = ref(null)
const gaugeChart = ref(null)

// Chart instances
let donutChartInstance = null
let gaugeChartInstance = null

// Constrained JWT for direct Sparrow API calls
const sparrowApiUrl = computed(() => props.apiData?.kestrel_api_url || '')
const countryIso3 = computed(() => props.apiData?.country_iso3_code)

const { token: sparrowToken, fetchToken: fetchSparrowToken } = useSparrowAuth({
  countryIso3,
  capabilities: ['target_area.view', 'boundary.view:all', 'boundary.view_shading:all'],
})

// Target areas stats data from Sparrow API - store the full API response
const fetchedTargetAreas = ref([])

// Country stats data from Sparrow API
const countryStats = ref(null)

// Loading state - track both data sources
const countryStatsLoaded = ref(false)
const targetAreasFetchComplete = ref(false)
const chartsLoading = computed(() => !countryStatsLoaded.value || !targetAreasFetchComplete.value)

// Target Areas drill-down table state
const expandedTcgs = ref([])
const expandedTcs = ref([])
const viewState = computed(() => (window.location.href.includes('historical') ? 'all' : 'active'))
// Enriched table data with stats attached to each target area
const enrichedTableData = computed(() => {
  // Create a lookup map to populate stats from Sparrow data
  const statsMap = {}
  // Sparrow v2 API returns fields directly on the item (no `attributes` wrapper)
  fetchedTargetAreas.value.forEach(item => {
    const externalId = item.external_id || item.attributes?.external_id
    statsMap[externalId] = item.running_analytics || item.attributes?.running_analytics || {}
  })

  // Enrich the table data with stats
  return props.targetAreasTableData.map(tcg => {
    const enrichedTcs = tcg.training_centers.map(tc => {
      const taStats = tc.target_area ? (statsMap[tc.target_area.external_id] || {}) : {}
      return {
        ...tc,
        target_area: tc.target_area ? {
          ...tc.target_area,
          stats: taStats
        } : null
      }
    }).sort((a, b) => (a.name || '').localeCompare(b.name || ''))

    // Aggregate stats for the TCG from each TC's target area
    const tcgStats = {
      village_count: 0,
      red_village_count: 0,
      yellow_village_count: 0,
      green_village_count: 0,
      white_village_count: 0,
      surveyed_village_count: 0
    }

    enrichedTcs.forEach(tc => {
      const stats = tc.target_area?.stats
      if (stats) {
        tcgStats.village_count += stats.village_count || 0
        tcgStats.red_village_count += stats.red_village_count || 0
        tcgStats.yellow_village_count += stats.yellow_village_count || 0
        tcgStats.green_village_count += stats.green_village_count || 0
        tcgStats.white_village_count += stats.white_village_count || 0
        tcgStats.surveyed_village_count += stats.surveyed_village_count || 0
      }
    })

    return {
      ...tcg,
      training_centers: enrichedTcs,
      stats: tcgStats
    }
  }).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
})

function toggleTcgExpanded(tcgId) {
  const index = expandedTcgs.value.indexOf(tcgId)
  if (index === -1) {
    expandedTcgs.value.push(tcgId)
  } else {
    expandedTcgs.value.splice(index, 1)
    // Also collapse all TCs under this TCG
    const tcg = enrichedTableData.value.find(t => t.id === tcgId)
    if (tcg) {
      tcg.training_centers.forEach(tc => {
        const tcIndex = expandedTcs.value.indexOf(tc.id)
        if (tcIndex !== -1) {
          expandedTcs.value.splice(tcIndex, 1)
        }
      })
    }
  }
}

function toggleTcExpanded(tcId) {
  const index = expandedTcs.value.indexOf(tcId)
  if (index === -1) {
    expandedTcs.value.push(tcId)
  } else {
    expandedTcs.value.splice(index, 1)
  }
}

function navigateToTargetArea(trainingCenterId) {
  window.location.href = `/training_centers/${trainingCenterId}/target_area`
}

onMounted(async () => {
  try {
    await fetchSparrowToken()
  } catch (err) {
    console.error('[AchieveDashboardStats] Failed to fetch Sparrow token:', err)
  }
  await Promise.all([fetchCountryStats(), fetchTargetAreaAnalytics()])
})

// Fetches per-TA running_analytics from Sparrow to populate the drill-down
// table and target-area stat boxes below the map. The Kestrel map handles its
// own popup stats — this fetch is purely for the dashboard sections.
async function fetchTargetAreaAnalytics() {
  const externalIds = props.targetAreasData
    .map((tc) => tc.target_area_external_id)
    .filter(Boolean)

  if (!externalIds.length) {
    targetAreasFetchComplete.value = true
    return
  }

  const batchSize = 100
  const rawApiData = []

  try {
    for (let i = 0; i < externalIds.length; i += batchSize) {
      const batch = externalIds.slice(i, i + batchSize)
      const result = await SparrowService.fetchTargetAreas(batch, {
        token: sparrowToken.value,
        sparrowApiUrl: sparrowApiUrl.value,
      })
      // v2 direct API returns { data: [...], meta: {...} }; v1 proxy may return array directly
      const items = Array.isArray(result) ? result : (result?.data || [])
      if (!items.length || items.error) continue
      rawApiData.push(...items)
      fetchedTargetAreas.value = [...rawApiData]
    }
  } catch (err) {
    console.error('[AchieveDashboardStats] Error fetching TA analytics:', err)
  } finally {
    targetAreasFetchComplete.value = true
  }
}

function getStats(item) {
  return item.running_analytics || item.attributes?.running_analytics || {}
}

const totalVillages = computed(() => {
  if (countryStats.value?.stats?.total_villages !== undefined) {
    return countryStats.value.stats.total_villages
  }
  return 0
})

const targetVillages = computed(() => {
  return fetchedTargetAreas.value.reduce((sum, item) => {
    const stats = getStats(item)
    return sum + (stats.village_count || 0)
  }, 0)
})

const redVillages = computed(() => {
  if (countryStats.value?.stats?.red !== undefined) {
    return countryStats.value.stats.red
  }
  return 0
  // return fetchedTargetAreas.value.reduce((sum, item) => {
  //   const stats = getStats(item)
  //   return sum + (stats.red_village_count || 0)
  // }, 0)
})

const yellowVillages = computed(() => {
  if (countryStats.value?.stats?.yellow !== undefined) {
    return countryStats.value.stats.yellow
  }
  return 0
  // return fetchedTargetAreas.value.reduce((sum, item) => {
  //   const stats = getStats(item)
  //   return sum + (stats.yellow_village_count || 0)
  // }, 0)
})

const greenVillages = computed(() => {
  if (countryStats.value?.stats?.green !== undefined) {
    return countryStats.value.stats.green
  }
  return 0
  // return fetchedTargetAreas.value.reduce((sum, item) => {
  //   const stats = getStats(item)
  //   return sum + (stats.green_village_count || 0)
  // }, 0)
})

const whiteVillages = computed(() => {
  if (countryStats.value?.stats?.grey !== undefined) {
    return countryStats.value.stats.grey
  }
  return 0
})

const villagesWithData = computed(() => {
  return redVillages.value + yellowVillages.value + greenVillages.value
})

const surveyedVillages = computed(() => {
  return fetchedTargetAreas.value.reduce((sum, item) => {
    const stats = getStats(item)
    return sum + (stats.surveyed_village_count || 0)
  }, 0)
})

const targetAreaCount = computed(() => {
  return fetchedTargetAreas.value.length
})

const surveyCompletePercent = computed(() => {
  if (totalVillages.value === 0) return 0
  return Math.round((surveyedVillages.value / targetVillages.value) * 100)
})

const villagesWithDataPercent = computed(() => {
  if (totalVillages.value === 0) return 0
  return ((villagesWithData.value / totalVillages.value) * 100).toFixed(2)
})

const totalChurches = computed(() => {
  return props.churchPresenceStats?.total_churches || 0
})

const churchesInRedVillages = computed(() => {
  return props.churchPresenceStats?.churches_red || 0
})

const churchesInYellowVillages = computed(() => {
  return props.churchPresenceStats?.churches_yellow || 0
})

const churchesInGreenVillages = computed(() => {
  return props.churchPresenceStats?.churches_green || 0
})

const churchesRedPercent = computed(() => {
  if (totalChurches.value === 0) return 0
  return ((churchesInRedVillages.value / totalChurches.value) * 100).toFixed(1)
})

const churchesYellowPercent = computed(() => {
  if (totalChurches.value === 0) return 0
  return ((churchesInYellowVillages.value / totalChurches.value) * 100).toFixed(1)
})

const churchesGreenPercent = computed(() => {
  if (totalChurches.value === 0) return 0
  return ((churchesInGreenVillages.value / totalChurches.value) * 100).toFixed(1)
})

const churchesInWhiteVillages = computed(() => {
  return props.churchPresenceStats?.churches_white || 0
})

const churchesWhitePercent = computed(() => {
  if (totalChurches.value === 0) return 0
  return ((churchesInWhiteVillages.value / totalChurches.value) * 100).toFixed(1)
})

// Render charts once when all data is loaded
watch(chartsLoading, (loading) => {
  if (!loading) {
    nextTick(() => {
      updateDonutChart()
      updateGaugeChart()
    })
  }
})

function toggleViewState() {
  let url = window.location.href.includes('/historical') ? window.location.href.replace('/historical', '') : window.location.href + '/historical'
  window.location.href = url
}

// Format numbers with commas
function formatNumber(num) {
  return num.toLocaleString()
}

// Format numbers in compact notation (e.g., 9.01K)
function formatCompactNumber(num) {
  if (num >= 1000) {
    return (num / 1000).toFixed(2) + 'K'
  }
  return num.toLocaleString()
}

async function fetchCountryStats() {
  const iso3 = props.targetAreasData?.[0]?.country_iso3_code || props.apiData?.country_iso3_code
  if (!iso3) {
    console.warn('No country ISO3 code available to fetch country stats')
    countryStatsLoaded.value = true
    return
  }

  try {
    const response = await SparrowService.fetchCountryStats(iso3)
    if (response.error) {
      console.warn('Error from API:', response.error)
      return
    }

    console.log('countryStats response', response)
    if (response) {
      countryStats.value = response
    }
  } catch (error) {
    console.warn('Error fetching country stats:', error.message)
  } finally {
    countryStatsLoaded.value = true
  }
}

function updateDonutChart() {
  if (!donutChart.value) return

  // Destroy existing chart if it exists
  if (donutChartInstance) {
    donutChartInstance.destroy()
  }

  Chart.register(Tooltip, CategoryScale, LinearScale, Title, DoughnutController, ArcElement, Legend, ChartDataLabels)

  const chartData = {
    labels: ['Yellow', 'Red', 'Green'],
    datasets: [{
      label: 'Villages',
      data: [yellowVillages.value, redVillages.value, greenVillages.value],
      backgroundColor: ['#fbbf24', '#ef4444', '#10b981'],
      borderColor: ['#f59e0b', '#dc2626', '#059669'],
      borderWidth: 2
    }]
  }

  donutChartInstance = new Chart(donutChart.value, {
    type: 'doughnut',
    data: chartData,
    plugins: [ChartDataLabels],
    options: {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '70%',
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              const value = context.parsed || 0
              const total = context.dataset.data.reduce((a, b) => a + b, 0)
              const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0
              return ` Villages: ${value.toLocaleString()} (${percentage}%)`
            }
          }
        },
        datalabels: {
          display: true,
          color: '#1f2937',
          font: {
            weight: 'bold',
            size: 12
          },
          formatter: (value, ctx) => {
            const total = ctx.dataset.data.reduce((a, b) => a + b, 0)
            if (total === 0) return ''
            const percentage = ((value / total) * 100).toFixed(1)
            return percentage > 5 ? `${percentage}%` : ''
          }
        }
      }
    }
  })
}

function updateGaugeChart() {
  if (!gaugeChart.value) return

  // Destroy existing chart if it exists
  if (gaugeChartInstance) {
    gaugeChartInstance.destroy()
  }

  Chart.register(Tooltip, CategoryScale, LinearScale, Title, DoughnutController, ArcElement, Legend, ChartDataLabels)

  // ensure non-negative values
  const villagesWithoutData = Math.max(0, totalVillages.value - villagesWithData.value)

  const chartData = {
    labels: ['With Data', 'Village Count'],
    datasets: [{
      label: 'Villages',
      data: [villagesWithData.value, villagesWithoutData],
      backgroundColor: ['#3b82f6', '#e5e7eb'],
      borderColor: ['#2563eb', '#d1d5db'],
      borderWidth: 0
    }]
  }

  gaugeChartInstance = new Chart(gaugeChart.value, {
    type: 'doughnut',
    data: chartData,
    plugins: [ChartDataLabels],
    options: {
      responsive: true,
      maintainAspectRatio: false,
      circumference: 180,
      rotation: -90,
      cutout: '60%',
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              const label = context.label || ''
              const value = context.parsed || 0
              return `${label}: ${value.toLocaleString()}`
            }
          }
        },
        datalabels: {
          display: false
        }
      }
    }
  })
}
</script>

<style scoped>
.achieve-dashboard {
  width: 100%;
}

.charts-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>

